import React, { useState } from 'react';
import { api } from '../services/api';

const BASE_ROLE_OPTIONS = [
  { code: 'role-acct-mgmt', label: 'Account Management' },
  { code: 'role-key-mgmt', label: 'Key Management' },
];

const UserManagement = ({ isOrg, canManageAccount, orgUsers, currentUserEmail, onUpdated, canAssignSupport, canAssignInternal }) => {
  const [editUserId, setEditUserId] = useState(null);
  const [editForm, setEditForm] = useState({ full_name: '', roles: [] });
  const [editUserInternal, setEditUserInternal] = useState(false);
  const [editUserInternalOriginal, setEditUserInternalOriginal] = useState(false);
  const [status, setStatus] = useState({ type: '', message: '' });
  const [confirmRemoveUser, setConfirmRemoveUser] = useState(null);
  const roleOptions = canAssignSupport
    ? [...BASE_ROLE_OPTIONS, { code: 'role-support', label: 'Support' }]
    : BASE_ROLE_OPTIONS;

  if (!isOrg) {
    return (
      <div className="card">
        <div className="card-body">
          <h5 className="card-title">Users</h5>
          <p className="text-muted mb-0">Individual accounts cannot manage additional users.</p>
        </div>
      </div>
    );
  }
  if (!canManageAccount) {
    return (
      <div className="card">
        <div className="card-body">
          <h5 className="card-title">Users</h5>
          <p className="text-muted mb-0">You do not have permission to manage users.</p>
        </div>
      </div>
    );
  }

  const startEdit = (user) => {
    setEditUserId(user.id);
    setEditForm({
      full_name: user.full_name || '',
      roles: user.roles || [],
    });
    const internalValue = Boolean(user.is_internal);
    setEditUserInternal(internalValue);
    setEditUserInternalOriginal(internalValue);
    setStatus({ type: '', message: '' });
  };

  const toggleRole = (role) => {
    if (role === 'role-support' && !editUserInternal) {
      setStatus({ type: 'danger', message: 'Support role requires an internal user.' });
      return;
    }
    setEditForm((prev) => ({
      ...prev,
      roles: prev.roles.includes(role)
        ? prev.roles.filter((r) => r !== role)
        : [...prev.roles, role],
    }));
  };

  const saveUser = async () => {
    if (!editUserId) {
      setStatus({ type: 'danger', message: 'User id missing; refresh and try again.' });
      return;
    }
    if (editForm.roles.length === 0) {
      setStatus({ type: 'danger', message: 'Select at least one role.' });
      return;
    }

    try {
      const payload = {
        full_name: editForm.full_name,
        roles: editForm.roles,
      };
      if (canAssignInternal && editUserInternal !== editUserInternalOriginal) {
        payload.is_internal = editUserInternal;
      }
      await api.put(`/acct-mgmt/v1/account/users/${editUserId}`, payload);
      setStatus({ type: 'success', message: 'User updated.' });
      setEditUserId(null);
      onUpdated();
    } catch (err) {
      setStatus({ type: 'danger', message: err.response?.data?.error || 'Failed to update user.' });
    }
  };

  const isCurrentUser = (user) => {
    if (!user?.email || !currentUserEmail) return false;
    return user.email.toLowerCase() === currentUserEmail.toLowerCase();
  };

  const removeUser = async (user) => {
    if (isCurrentUser(user)) {
      setStatus({ type: 'warning', message: 'You cannot remove yourself from the organization.' });
      return;
    }
    if (!user.id) {
      setStatus({ type: 'danger', message: 'User id missing; refresh and try again.' });
      return;
    }
    setConfirmRemoveUser(user);
  };

  const sortedUsers = [...orgUsers].sort((a, b) => {
    if (isCurrentUser(a)) return -1;
    if (isCurrentUser(b)) return 1;
    return (a.full_name || a.email || '').localeCompare(b.full_name || b.email || '');
  });

  const formatLastLogin = (value) => {
    if (!value) return 'Never';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'Never';
    return date.toLocaleString();
  };

  return (
    <div>
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div>
          <h2 className="h4 fw-bold mb-1">Users</h2>
          <div className="text-muted">Manage teammates and permissions.</div>
        </div>
      </div>

      {status.message && (
        <div className={`alert alert-${status.type}`} role="alert">{status.message}</div>
      )}

      <div className="card">
        <div className="card-body">
            <div className="mb-3">
              <h5 className="card-title">Organization User List</h5>
            </div>
          <div className="table-responsive">
            <table className="table align-middle">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Roles</th>
                  <th>Last Login</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedUsers.map((user) => (
                  <tr key={user.email}>
                    <td>
                      <div className="fw-semibold">
                        {user.full_name || 'Unnamed user'}
                        {isCurrentUser(user) && (
                          <span className="badge bg-primary ms-2">Me</span>
                        )}
                        {user.is_internal && (
                          <span className="badge bg-info-subtle text-dark border ms-2">Internal</span>
                        )}
                      </div>
                      <div className="text-muted small">{user.email}</div>
                    </td>
                    <td>
                      {(user.roles || []).length === 0 ? (
                        <span className="text-muted">No roles</span>
                      ) : (
                        user.roles.map((role) => (
                          <span
                            key={role}
                            className={`badge ${role === 'role-support' ? 'bg-success text-white' : 'bg-light text-dark border'} me-1`}
                          >
                            {roleOptions.find((r) => r.code === role)?.label || role}
                          </span>
                        ))
                      )}
                    </td>
                    <td className="text-muted small">{formatLastLogin(user.last_login_at)}</td>
                    <td className="text-end">
                      <button
                        className="btn btn-sm btn-outline-primary me-2"
                        onClick={() => startEdit(user)}
                      >
                        Edit
                      </button>
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => removeUser(user)}
                        disabled={isCurrentUser(user)}
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
                {orgUsers.length === 0 && (
                  <tr>
                    <td colSpan="3" className="text-muted text-center">No users found for this organization.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {editUserId && (
        <div className="card mt-3">
          <div className="card-body">
            <h5 className="card-title">Edit User</h5>
            <div className="mb-3">
              <label className="form-label">Full Name</label>
              <input
                className="form-control"
                value={editForm.full_name}
                onChange={(e) => setEditForm({ ...editForm, full_name: e.target.value })}
              />
            </div>
            <div className="mb-3">
              <label className="form-label">Roles</label>
              <div className="d-flex flex-wrap gap-3">
                {roleOptions.map((role) => (
                  <div className="form-check form-switch finbase-toggle" key={role.code}>
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id={`role-${role.code}`}
                      checked={editForm.roles.includes(role.code)}
                      disabled={role.code === 'role-support' && !editUserInternal}
                      onChange={() => toggleRole(role.code)}
                    />
                    <label className="form-check-label" htmlFor={`role-${role.code}`}>
                      {role.label}
                    </label>
                    {role.code === 'role-support' && !editUserInternal && (
                      <small className="text-muted ms-2">Requires internal user</small>
                    )}
                  </div>
                ))}
              </div>
            </div>
            {canAssignInternal && (
              <div className="mb-3">
                <label className="form-label">Internal User</label>
                <div className="form-check form-switch finbase-toggle">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="user-internal-flag"
                    checked={editUserInternal}
                    onChange={() => {
                      const nextValue = !editUserInternal;
                      if (!nextValue && editForm.roles.includes('role-support')) {
                        setStatus({ type: 'danger', message: 'Support role requires an internal user.' });
                        return;
                      }
                      setEditUserInternal(nextValue);
                    }}
                  />
                  <label className="form-check-label" htmlFor="user-internal-flag">
                    Mark user as internal (requires internal email domain)
                  </label>
                </div>
              </div>
            )}
            <div className="d-flex gap-2">
              <button className="btn btn-primary" onClick={saveUser}>Save User</button>
              <button className="btn btn-outline-secondary" onClick={() => setEditUserId(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
      {confirmRemoveUser && (
        <>
          <div className="modal-backdrop show"></div>
          <div className="modal d-block" tabIndex="-1" role="dialog" aria-modal="true">
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Remove user?</h5>
                  <button
                    type="button"
                    className="btn-close"
                    aria-label="Close"
                    onClick={() => setConfirmRemoveUser(null)}
                  ></button>
                </div>
                <div className="modal-body">
                  <p className="mb-0">
                    Remove <strong>{confirmRemoveUser.email}</strong> from this organization?
                  </p>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setConfirmRemoveUser(null)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={async () => {
                      const targetUser = confirmRemoveUser;
                      setConfirmRemoveUser(null);
                      try {
                        const response = await api.delete(`/acct-mgmt/v1/account/users/${targetUser.id}`);
                        if (response.data?.support_portal_identity_cleanup_warning) {
                          setStatus({ type: 'warning', message: response.data.support_portal_identity_cleanup_warning });
                        } else {
                          setStatus({ type: 'success', message: 'User removed.' });
                        }
                        onUpdated();
                      } catch (err) {
                        setStatus({ type: 'danger', message: err.response?.data?.error || 'Failed to remove user.' });
                      }
                    }}
                  >
                    Yes, Remove
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default UserManagement;
